import React from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Target, Cpu, BarChart3, Users } from "lucide-react";

const features = [
  {
    icon: Target,
    title: "Automation First",
    description: "We automate repetitive tasks to boost productivity and reduce human error.",
    color: "blue",
    delay: 0,
  },
  {
    icon: Cpu,
    title: "Smart Technology",
    description: "Leveraging cutting-edge tech to build intelligent, future-proof systems.",
    color: "purple",
    delay: 0.1,
  },
  {
    icon: BarChart3,
    title: "Performance Driven",
    description: "Our solutions are optimized for speed, reliability, and peak performance.",
    color: "cyan",
    delay: 0.2,
  },
  {
    icon: Users,
    title: "Scalable Growth",
    description: "Built to scale seamlessly as your business grows and evolves.",
    color: "green",
    delay: 0.3,
  },
];

const AnimatedCounter = ({ target, suffix = "", isInView }) => {
  const [count, setCount] = React.useState(0);
  
  React.useEffect(() => {
    if (!isInView) return;
    
    const duration = 2000;
    const steps = 60;
    const increment = target / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    
    return () => clearInterval(timer);
  }, [target, isInView]);
  
  return <span>{count}{suffix}</span>;
};

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  const colorClasses = {
    blue: { bg: "bg-blue-500/20", text: "text-blue-400", border: "border-blue-500/30" },
    purple: { bg: "bg-purple-500/20", text: "text-purple-400", border: "border-purple-500/30" },
    cyan: { bg: "bg-cyan-500/20", text: "text-cyan-400", border: "border-cyan-500/30" },
    green: { bg: "bg-emerald-500/20", text: "text-emerald-400", border: "border-emerald-500/30" },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <section
      id="about"
      ref={ref}
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="about-section"
    >
      {/* Animated section divider */}
      <motion.div 
        className="section-divider mb-24"
        initial={{ scaleX: 0 }}
        animate={isInView ? { scaleX: 1 } : {}}
        transition={{ duration: 1 }}
      />

      {/* Background decorations */}
      <motion.div
        className="absolute top-1/4 -left-32 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"
        animate={{ 
          y: [0, 50, 0],
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left - Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            {/* Section label with animation */}
            <motion.span 
              className="text-sm uppercase tracking-widest text-blue-400 font-medium mb-4 inline-flex items-center gap-2"
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.2 }}
            >
              <motion.span
                className="w-8 h-px bg-blue-400"
                initial={{ width: 0 }}
                animate={isInView ? { width: 32 } : {}}
                transition={{ delay: 0.4, duration: 0.5 }}
              />
              About Mighty Routes
            </motion.span>

            <motion.h2 
              className="text-3xl md:text-5xl font-bold tracking-tight mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 }}
            >
              Building Powerful Software for{" "}
              <span className="text-gradient">Call Centers</span>
            </motion.h2>

            <motion.p 
              className="text-lg text-slate-400 mb-8 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4 }}
            >
              At Mighty Routes, we specialize in creating powerful, tailored software 
              systems designed specifically for call centers, BPO companies, and growing 
              businesses. Our solutions drive efficiency, streamline operations, and 
              empower teams to achieve more.
            </motion.p>

            <motion.p 
              className="text-slate-400 mb-10 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.5 }}
            >
              From custom CRM systems and HRMS platforms to DNC scrubbers and workflow 
              automation tools, we build software that transforms how businesses operate. 
              Our team combines deep industry expertise with cutting-edge technology to 
              deliver solutions that scale.
            </motion.p>

            {/* Animated Stats row */}
            <motion.div 
              className="grid grid-cols-3 gap-6"
              variants={containerVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
            >
              {[
                { value: 5, suffix: "+", label: "Years Experience" },
                { value: 100, suffix: "+", label: "Happy Clients" },
                { value: 150, suffix: "+", label: "Solutions Built" },
              ].map((stat, index) => (
                <motion.div 
                  key={stat.label}
                  className="text-center group cursor-pointer"
                  variants={itemVariants}
                  whileHover={{ scale: 1.05, y: -5 }}
                >
                  <motion.div 
                    className="text-3xl md:text-4xl font-bold text-gradient"
                    initial={{ scale: 0 }}
                    animate={isInView ? { scale: 1 } : {}}
                    transition={{ delay: 0.6 + index * 0.1, type: "spring" }}
                  >
                    <AnimatedCounter target={stat.value} suffix={stat.suffix} isInView={isInView} />
                  </motion.div>
                  <div className="text-sm text-slate-400 mt-1 group-hover:text-slate-300 transition-colors">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right - Feature cards */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-6"
          >
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.4 + feature.delay }}
                whileHover={{ 
                  scale: 1.05, 
                  y: -5,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
                }}
                className={`glass-card p-6 cursor-pointer border ${colorClasses[feature.color].border} hover:border-opacity-60 transition-all duration-300`}
                data-testid={`about-feature-${index}`}
              >
                <motion.div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                    colorClasses[feature.color].bg
                  } ${colorClasses[feature.color].text}`}
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                >
                  <feature.icon size={24} />
                </motion.div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed">
                  {feature.description}
                </p>
                
                {/* Animated underline */}
                <motion.div
                  className={`h-0.5 mt-4 rounded-full ${colorClasses[feature.color].bg}`}
                  initial={{ width: 0 }}
                  whileHover={{ width: "100%" }}
                  transition={{ duration: 0.3 }}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
